import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { MdAccessTime, MdInfoOutline, MdCheckCircle } from 'react-icons/md';
// moodleApi import dropped — this page no longer calls Moodle directly.
// `localStorage.moodleToken` is still read on lines 182/215 as a fallback
// auth param for /my/time-exclusion + /export endpoints; those should
// switch to sessionToken in a follow-up (Phase 2D step 3 BE work).
import {
  calculateSupervideoLearningTime,
  calculateQuizLearningTime,
  groupUserGradeByCourseModule,
  isTimestampTodayUTC7,
} from 'utils/elearning.utils';
import { Container, Modal, Table } from 'react-bootstrap';
import {
  Alert,
  PrimaryButton,
  Spinner,
  colors,
  radius,
  shadow,
  spacing,
  typography,
} from 'design-system';
import { formatTime } from 'utils/commonUtils';
import DetailActivityReport from '../components/DetailActivityReport';
import elearningApi from 'api/elearningApi';
import { PATH } from 'constants/path';
import { toastWrapper } from 'utils';
import { applySummaryResult } from 'utils/completion-summary-view.util';
import { COURSE_MODULES, ELEARNING_POLL_INTERVAL } from 'constants/driving-elearning.constant';
import { useDispatch, useSelector } from 'react-redux';
import { selectElearningData, updateElearningData } from 'store/elearning.slice';
import axios from 'axios';

const MOBILE = '@media (max-width: 768px)';

function ElearningStudentResultPage() {
  const dispatch = useDispatch();
  const elearningData = useSelector(selectElearningData);
  const {
    elearningCourses,
    elearningCoursesContents,
    elearningUser,
    elearningSettings,
    bookMapaData,
    bookModuleSettings,
    elearningGrades,
    quizAttempts,
    activityReport,
    totalTodayTime: storeTotalTodayTime,
    timePerCourse: storeTimePerCourse,
    todayTimeUpdatedAt: storeUpdatedAt,
  } = elearningData;
  const [loading, setLoading] = useState(true);
  const [, setQuizAttemptsLoading] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [reportDownloading, setReportDownloading] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  // Tóm tắt hoàn thành do backend tính — bảng "Kết quả tổng hợp" và thẻ
  // đặt cabin chỉ hiển thị số này (không tự tính trong trình duyệt).
  const [completionSummary, setCompletionSummary] = useState(null);
  const [summaryError, setSummaryError] = useState(null);
  const [reloadingSummary, setReloadingSummary] = useState(false);
  const [serverTime, setServerTime] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [requestingCabin, setRequestingCabin] = useState(false);
  const [cabinBookingInfo, setCabinBookingInfo] = useState(null);
  const [checkingCompletion, setCheckingCompletion] = useState(false);
  // True only after user has explicitly clicked "Kiểm tra trạng thái"
  // AND the fresh fetch confirmed every assigned course is completed.
  // Resets on reload — student must re-verify per session. We don't
  // pre-seed this from cached Redux data because the description
  // promises "bấm Kiểm tra trạng thái … nút Đăng ký lịch cabin sẽ
  // hiện" — bypassing the click would make that promise a lie.
  const [verifiedCompleted, setVerifiedCompleted] = useState(false);

  const quizIds = useMemo(() => {
    if (!elearningGrades) return [];
    return Object.values(elearningGrades || {}).reduce((acc, grade) => {
      if (grade?.modules) {
        Object.keys(grade?.modules).forEach((cmid) => {
          const module = grade.modules[cmid];
          if (module?.modname === 'quiz' && module?.quiztimelimit === 0) {
            acc.push(module?.cminstance);
          }
        });
      }
      return acc;
    }, []);
  }, [elearningGrades]);

  useEffect(() => {
    const fetchQuizAttempts = async () => {
      // Skip if we don't have necessary data or if quizIds is empty
      if (!quizIds?.length || !elearningUser?.elearningUserId) return;

      // Skip if we already have data for these quizzes
      const hasExistingData = quizIds.every(id => quizAttempts?.[id]);
      if (hasExistingData) return;

      try {
        setQuizAttemptsLoading(true);
        // BATCH: trước đây map N call song song getUserQuizAttempts —
        // 20 quiz = 20 request hit BE → 20 Moodle WS call cùng giây,
        // quá tải. Endpoint /quiz/attempts/batch BE đã có, gom 1 call
        // trả `{ [quizId]: { attempts } }` cùng shape.
        const res = await elearningApi.getBatchQuizAttempts(
          elearningUser.elearningUserId,
          quizIds,
        );
        const quizAttempts = res?.data || {};

        dispatch(
          updateElearningData({
            quizAttempts,
          })
        );
      } catch (error) {
        console.error('Error fetching quiz attempts:', error);
        toastWrapper(
          'Có lỗi xảy ra khi tải dữ liệu bài kiểm tra của bạn!',
          'error'
        );
      } finally {
        setQuizAttemptsLoading(false);
      }
    };

    fetchQuizAttempts();
    // KHÔNG đưa `quizAttempts` vào deps: dispatch updateElearningData ở trên
    // đổi quizAttempts → effect rerun → nếu batch response thiếu key cho 1
    // quizId nào đó (quiz chưa có attempt bị BE bỏ qua), `hasExistingData`
    // (quizIds.every(id => quizAttempts?.[id])) MÃI false → refetch vô hạn,
    // hammer /quiz/attempts/batch + kẹt loading trang kết quả. Chỉ fetch khi
    // quizIds / user thay đổi.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizIds, elearningUser?.elearningUserId]);

  useEffect(() => {
    const loadUserElearningData = async (lessonIds) => {
      try {
        setLoading(true);
        const [userCourseGradeRes, activityReportRes] = await Promise.all([
          elearningApi.getUserCourseGrade([elearningUser?.elearningUserId]),
          elearningApi.getElearningActivityReport(lessonIds, [
            elearningUser?.elearningUserId,
          ]),
        ]);
        const elearningGrades = groupUserGradeByCourseModule(
          userCourseGradeRes?.data
        )[elearningUser?.elearningUserId]?.courses;
        const activityReport =
          activityReportRes?.data?.[elearningUser?.elearningUserId];
        dispatch(
          updateElearningData({
            elearningGrades,
            activityReport,
          })
        );
      } catch (error) {
        toastWrapper('Có lỗi xảy ra khi tải dữ liệu học tập của bạn!', 'error');
      } finally {
        setLoading(false);
      }
    };

    if (elearningUser) {
      const lessonIds = elearningUser?.elearningLessons;
      loadUserElearningData(lessonIds);
    }
  }, [elearningUser]);


  // "Kiểm tra điều kiện" — hit BE /elearning/my/request-cabin-booking,
  // which runs verifyStudentCompletionLive against Moodle. BE is the
  // source of truth (cron + cap-suspend + ngay_hoc_hih downstream
  // all key off the same elearningStatus). On success it returns a
  // signed redirectUrl with a 15-min JWT — we store that and swap
  // the button so the next click just navigates (no second API call).
  //
  // FE allCoursesCompleted (table predicate) only gates VISIBILITY of
  // the card; this handler is the real gate.
  const handleCheckCompletion = async () => {
    if (checkingCompletion) return;
    setCheckingCompletion(true);
    try {
      const res = await elearningApi.requestCabinBooking();
      const data = res?.data || {};
      if (data?.redirectUrl) {
        setCabinBookingInfo(data);
        setVerifiedCompleted(true);
        toastWrapper('Đã xác nhận đủ điều kiện — bấm "Đăng ký lịch cabin" để tiếp tục.', 'success');
      } else {
        setVerifiedCompleted(false);
        toastWrapper('Phản hồi không hợp lệ từ máy chủ', 'error');
      }
    } catch (err) {
      setVerifiedCompleted(false);
      const code = err?.response?.data?.code;
      const message = err?.response?.data?.message;
      if (code === 'NOT_COMPLETED') {
        toastWrapper(message || 'Bạn chưa hoàn thành tất cả môn học của khoá', 'warning');
      } else if (err?.response?.status === 429) {
        toastWrapper(message || 'Bạn đã gửi quá nhiều yêu cầu. Vui lòng thử lại sau.', 'warning');
      } else {
        console.error('handleCheckCompletion error:', err);
        toastWrapper(message || 'Không thể kiểm tra điều kiện. Vui lòng thử lại sau.', 'error');
      }
    } finally {
      setCheckingCompletion(false);
    }
  };

  // "Đăng ký lịch cabin" — pure redirect to the URL handleCheckCompletion
  // already obtained. No second API call: the 15-min JWT in the URL is
  // verified by dat.uniapp.vn/cabin's CalcomPage on landing. If the JWT
  // expired, dat-side will surface the error — HV bấm "Kiểm tra điều
  // kiện" lại để mint token mới.
  const handleRequestCabin = () => {
    if (!cabinBookingInfo?.redirectUrl) return;
    window.location.href = cabinBookingInfo.redirectUrl;
  };

  const refreshTodayTime = async () => {
    setRefreshing(true);
    try {
      const res = await elearningApi.getMyTodayTime();
      const { totalTodayTime, timeLimitPerDay, isLimitExceeded, timePerCourse } = res.data;
      setServerTime(res.data);
      const now = Date.now();
      setLastUpdated(now);
      dispatch(updateElearningData({
        totalTodayTime,
        timeLimitPerDay,
        isLimitExceeded,
        timePerCourse,
        todayTimeUpdatedAt: now,
      }));
    } catch (err) {
      console.error('Error fetching today time:', err);
      if (err?.response?.status === 429) {
        toastWrapper(err?.response?.data?.message || 'Bạn đã gửi quá nhiều yêu cầu. Vui lòng thử lại sau.', 'warning');
      } else {
        toastWrapper('Không thể cập nhật thời gian học. Vui lòng thử lại.', 'error');
      }
    } finally {
      setRefreshing(false);
    }
  };

  const summaryRef = useRef(null);
  const loadCompletionSummary = React.useCallback(async (fresh = false) => {
    if (fresh) setReloadingSummary(true);
    let result;
    try {
      const res = await elearningApi.getMyCompletionSummary(fresh);
      if (!res?.data?.perCourse) throw new Error('empty');
      result = { ok: true, data: res.data };
    } catch (error) {
      result = { ok: false, error };
    }
    const next = applySummaryResult({ summary: summaryRef.current }, result);
    summaryRef.current = next.summary;
    setCompletionSummary(next.summary);
    setSummaryError(next.error);
    if (next.notify) {
      const e = result.error;
      toastWrapper(
        e?.response?.data?.message || 'Không tải lại được kết quả, vẫn hiển thị số lần trước.',
        'warning',
      );
    }
    if (fresh) setReloadingSummary(false);
  }, []);

  // Tải ngay khi có hồ sơ HV (API dùng phiên đăng nhập, không cần _id).
  const summaryUserKey = elearningUser ? (elearningUser._id || elearningUser.elearningUserId || 'session') : null;
  useEffect(() => {
    if (!summaryUserKey) return;
    loadCompletionSummary(false);
  }, [summaryUserKey, loadCompletionSummary]);




  const handleDownloadReport = async () => {
    setShowReportModal(false);

    const moodleToken = localStorage.getItem('moodleToken');
    if (!moodleToken && !elearningUser?.elearningUserId) {
      toastWrapper('Không tìm thấy thông tin xác thực. Vui lòng tải lại trang.', 'error');
      return;
    }

    setReportDownloading(true);
    try {
      const hostname = window.location.hostname;
      const apiBaseUrl = hostname?.split('.')?.includes('lms2')
        ? process.env.REACT_APP_API_2_URL
        : process.env.REACT_APP_API_URL;

      const params = { format: 'pdf' };
      if (moodleToken) {
        params.moodleToken = moodleToken;
      } else {
        params.studentId = elearningUser.elearningUserId;
      }

      const res = await axios.get(`${apiBaseUrl}/elearning/my/daily-time-report`, {
        params,
        responseType: 'blob',
        timeout: 60000,
      });

      const blob = new Blob([res.data], { type: 'application/pdf' });

      // Tên file: ThoiGianHoc_<tên HV>_<tên khoá>_<ngày>.pdf
      const studentName = (elearningUser?.name || 'HocVien').replace(/\s+/g, '_');
      const courseName = (elearningUser?.course?.name || 'Khoa').replace(/\s+/g, '_');
      const now = new Date(Date.now() + 7 * 60 * 60 * 1000);
      const dateStr = `${now.getUTCDate()}-${now.getUTCMonth() + 1}-${now.getUTCFullYear()}`;
      const filename = `ThoiGianHoc_${studentName}_${courseName}_${dateStr}.pdf`;

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);

      toastWrapper('Tải báo cáo thành công!', 'success');
    } catch (err) {
      if (err.response?.status === 429) {
        toastWrapper('Bạn đã vượt quá giới hạn 5 lần tải báo cáo/ngày. Vui lòng thử lại vào ngày mai.', 'warning');
      } else if (err.response?.status === 404) {
        toastWrapper('Không tìm thấy học viên hoặc tài khoản không hoạt động.', 'error');
      } else if (err.response?.status === 400) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          toastWrapper(json.message || 'Yêu cầu không hợp lệ.', 'error');
        } catch {
          toastWrapper('Yêu cầu không hợp lệ.', 'error');
        }
      } else {
        toastWrapper('Có lỗi xảy ra khi tải báo cáo. Vui lòng thử lại sau.', 'error');
      }
    } finally {
      setReportDownloading(false);
    }
  };

  const today = Date.now();
  const threshold = 15;

  const timePerCourseData = serverTime?.timePerCourse || storeTimePerCourse || [];

  const displayTimePerCourse = useMemo(() => {
    return timePerCourseData.map(({ courseId, timeSpent }) => ({
      courseId,
      coursename: elearningGrades?.[courseId]?.coursename || elearningCourses?.[courseId]?.fullname || `Khoá ${courseId}`,
      timeSpent,
    }));
  }, [timePerCourseData, elearningGrades, elearningCourses]);

  const totalTodayTime = serverTime?.totalTodayTime ?? storeTotalTodayTime ?? 0;

  // Mirror "Kết quả tổng hợp" table predicate. Gates CabinCard
  // visibility — HV only sees the card when every assigned course
  // already shows "Đã hoàn thành" on the FE table. BE still runs an
  // independent live-verify when HV clicks "Kiểm tra điều kiện"
  // (defense in depth) so a stale/spoofed FE state can't bypass.
  // Backend quyết định (cùng phép tính với nút "Kiểm tra điều kiện").
  const allCoursesCompleted = !!completionSummary?.isCompleted;

  // Lấy các giá trị `timePerQuestionInMinute` (phút/câu) cấu hình ở
  // ElearningSettings cho TỪNG course của HV → render notice động.
  //   - Tất cả course cùng 1 giá trị (case phổ biến) → "0.5 phút/câu"
  //   - Course có cấu hình khác nhau → "0.3–0.7 phút/câu (tuỳ môn)"
  //   - Không có config → fallback 0.5 (khớp default BE service).
  const timePerQuestionLabel = useMemo(() => {
    const values = new Set();
    Object.values(elearningSettings || {}).forEach((s) => {
      const v = s?.timePerQuestionInMinute;
      if (typeof v === 'number' && v > 0) values.add(v);
    });
    if (values.size === 0) return '0.5 phút/câu';
    const sorted = Array.from(values).sort((a, b) => a - b);
    if (sorted.length === 1) return `${sorted[0]} phút/câu`;
    return `${sorted[0]}–${sorted[sorted.length - 1]} phút/câu (tuỳ môn học)`;
  }, [elearningSettings]);

  if (!elearningGrades && elearningGrades !== null) {
    return (
      <PageWrap>
        <Container className='py-3 py-md-4'>
          <Alert variant='info'>
            Chưa có dữ liệu học tập. Vui lòng hoàn thành ít nhất 1 bài học và tải
            lại trang này
          </Alert>
        </Container>
      </PageWrap>
    );
  }

  // Không chờ lượt làm quiz (chỉ dùng cho hộp thoại giờ hôm nay, tải tuần tự
  // mọi quiz nên chậm) — bảng tổng hợp đã lấy số từ backend.
  const isPageLoading =
    loading ||
    !elearningUser ||
    !elearningCourses ||
    !elearningCoursesContents ||
    !elearningSettings ||
    bookMapaData === null ||
    (!completionSummary && !summaryError);

  return (
    <PageWrap>
      <Container className='py-3 py-md-4'>
        {isPageLoading ? (
          <LoadingBlock>
            <Spinner block label='Đang tải dữ liệu học tập...' />
            <LoadingChecklist>
              {[
                { loaded: !!elearningUser, label: 'Đang tải thông tin học viên...' },
                { loaded: !!elearningCourses && !!elearningCoursesContents, label: 'Đang tải danh sách môn học...' },
                { loaded: !loading && !!elearningGrades, label: 'Đang tải báo cáo điểm...' },
                { loaded: !loading && !!activityReport, label: 'Đang tải báo cáo hoạt động...' },
                { loaded: !!elearningSettings, label: 'Đang tải cấu hình môn học...' },
                { loaded: bookMapaData !== null, label: 'Đang tải dữ liệu giáo trình...' },
                { loaded: !!completionSummary || !!summaryError, label: 'Đang tính thời gian đã tích luỹ...' },
              ].map(({ loaded, label }) => (
                <LoadingChecklistItem key={label} $loaded={loaded}>
                  {loaded ? (
                    <CheckMark>✓</CheckMark>
                  ) : (
                    <Spinner size='xs' variant='secondary' />
                  )}
                  <span>{label}</span>
                </LoadingChecklistItem>
              ))}
            </LoadingChecklist>
          </LoadingBlock>
        ) : (
          <>
            <SectionHeader>
              <PageTitle>Thời gian học hôm nay</PageTitle>
              <HeaderActions>
                <UpdatedAtPill
                  title='Hệ thống tự cập nhật theo chu kỳ. Nhấn Làm mới để đồng bộ ngay.'
                >
                  <MdAccessTime size={14} />
                  <span>
                    {(lastUpdated || storeUpdatedAt) ? (
                      <>
                        Cập nhật lúc{' '}
                        <strong>{new Date(lastUpdated || storeUpdatedAt).toLocaleTimeString('vi-VN')}</strong>
                      </>
                    ) : (
                      <>Chưa cập nhật lần nào</>
                    )}
                    <UpdatedAtMeta>
                      · Cập nhật mỗi {Math.round(ELEARNING_POLL_INTERVAL / 60000)} phút
                    </UpdatedAtMeta>
                  </span>
                </UpdatedAtPill>
                <PrimaryButton
                  variant='secondary'
                  size='sm'
                  onClick={refreshTodayTime}
                  loading={refreshing}
                  loadingText='Đang tải...'
                >
                  Làm mới
                </PrimaryButton>
              </HeaderActions>
            </SectionHeader>

            {/* CabinCard CHỈ render khi FE table thấy mọi môn "Đã hoàn
                thành" (allCoursesCompleted). HV chưa xong → không hiện
                để khỏi gây nhầm. Khi hiện, "Kiểm tra điều kiện" là gate
                duy nhất: gọi BE /request-cabin-booking → BE live-verify
                Moodle + mint JWT. Pass → button swap "Đăng ký lịch
                cabin" → pure redirect (không call API lại). Reload page
                → verifiedCompleted reset → bấm kiểm tra lần nữa. */}
            {allCoursesCompleted && (
              <CabinCard>
                <CabinIcon><MdCheckCircle size={28} /></CabinIcon>
                <CabinBody>
                  <CabinTitle>Đăng ký lịch học cabin</CabinTitle>
                </CabinBody>
                {verifiedCompleted ? (
                  <PrimaryButton
                    variant='primary'
                    size='md'
                    onClick={handleRequestCabin}
                    disabled={!cabinBookingInfo?.redirectUrl}
                  >
                    Đăng ký lịch cabin
                  </PrimaryButton>
                ) : (
                  <PrimaryButton
                    variant='secondary'
                    size='md'
                    onClick={handleCheckCompletion}
                    loading={checkingCompletion}
                    loadingText='Đang kiểm tra...'
                    disabled={checkingCompletion}
                  >
                    Kiểm tra điều kiện
                  </PrimaryButton>
                )}
              </CabinCard>
            )}

            <NoticeCard>
              <NoticeIcon>
                <MdInfoOutline size={22} />
              </NoticeIcon>
              <NoticeBody>
                <NoticeTitle>Lưu ý về cách tính thời gian học</NoticeTitle>
                <NoticeList>
                  <li>
                    Các học phần <strong>Tự học</strong> sẽ được cập nhật
                    thời gian sau khi hoàn thành <strong>tất cả các chương
                    bài học</strong>.
                  </li>
                  <li>
                    Các môn học đã học đủ các phần nhưng <strong>Chưa đủ
                    giờ</strong> cần tích luỹ thêm bằng cách làm thêm câu
                    hỏi ôn tập. Quy tắc tính:{' '}
                    <strong>{timePerQuestionLabel}</strong> đã thực hiện.
                  </li>
                </NoticeList>
              </NoticeBody>
            </NoticeCard>

            <StyledTableWrap>
              <Table striped bordered hover responsive className='mb-0'>
                <thead>
                  <tr>
                    <th>Môn học</th>
                    <th>Thời gian tích luỹ</th>
                    <th>Chi tiết</th>
                  </tr>
                </thead>
                <tbody>
                  {displayTimePerCourse
                    .filter((item) => item.timeSpent > 0)
                    .map(({ courseId, coursename, timeSpent }) => (
                      <tr key={courseId}>
                        <td>{coursename}</td>
                        <td>{formatTime(timeSpent)}</td>
                        <td>
                          <PrimaryButton
                            variant='ghost'
                            size='sm'
                            onClick={() => setSelectedCourse(courseId)}
                          >
                            Xem
                          </PrimaryButton>
                        </td>
                      </tr>
                    ))}
                  <tr>
                    <td className='text-end'>
                      <strong>Tổng cộng</strong>
                    </td>
                    <td>
                      <strong>{formatTime(totalTodayTime)}</strong>
                    </td>
                    <td></td>
                  </tr>
                </tbody>
              </Table>
            </StyledTableWrap>

            <Modal show={showReportModal} onHide={() => setShowReportModal(false)} centered>
              <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: typography.size.lg, fontWeight: typography.weight.bold }}>
                  Tải báo cáo thời gian học
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <ModalText>
                  Bạn chỉ được tải báo cáo <strong>tối đa 5 lần/ngày</strong>.
                </ModalText>
                <NoticeBox>
                  <NoticeTitle>Lưu ý:</NoticeTitle>
                  <NoticeList>
                    <li>Thời gian học hôm nay chỉ được cập nhật vào báo cáo mỗi 30 phút nên có thể sai lệch với hiện tại.</li>
                    <li>Thời gian tải báo cáo có thể mất từ 1-5 phút.</li>
                  </NoticeList>
                </NoticeBox>
              </Modal.Body>
              <Modal.Footer>
                <PrimaryButton variant='secondary' size='sm' onClick={() => setShowReportModal(false)}>
                  Huỷ
                </PrimaryButton>
                <PrimaryButton
                  size='sm'
                  onClick={handleDownloadReport}
                  loading={reportDownloading}
                  loadingText='Đang tải...'
                >
                  Tải báo cáo
                </PrimaryButton>
              </Modal.Footer>
            </Modal>

            <PageTitle as='h2' $spaced>Báo cáo kết quả toàn khoá học</PageTitle>
            <DetailActivityReport
              elearningUser={elearningUser}
              elearningGrades={elearningGrades}
              activityReport={activityReport}
              elearningCourses={elearningCourses}
              elearningCoursesContents={elearningCoursesContents}
              completionSummary={completionSummary}
              summaryError={summaryError}
              onReloadSummary={() => loadCompletionSummary(true)}
              reloadingSummary={reloadingSummary}
            />

            {/* Modal chi tiết thời gian học hôm nay theo từng module */}
            <Modal
              show={!!selectedCourse}
              onHide={() => setSelectedCourse(null)}
              size='lg'
              centered
              scrollable
            >
              <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: typography.size.lg, fontWeight: typography.weight.bold }}>
                  Chi tiết thời gian học hôm nay &mdash;{' '}
                  {elearningGrades?.[selectedCourse]?.coursename || elearningCourses?.[selectedCourse]?.fullname || `Khoá ${selectedCourse}`}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {selectedCourse && (() => {
                  const course = elearningGrades?.[selectedCourse];
                  const modules = course?.modules || {};
                  const rows = Object.values(modules).map((mod) => {
                    let timeSpent = 0;
                    if (mod.modname === 'supervideo') {
                      timeSpent = calculateSupervideoLearningTime(
                        mod.mapa || [],
                        today,
                        threshold
                      );
                    } else if (mod.modname === 'quiz') {
                      timeSpent = calculateQuizLearningTime(
                        mod,
                        today,
                        elearningSettings?.[selectedCourse],
                        quizAttempts || {}
                      );
                    }
                    return { mod, timeSpent };
                  });

                  // Chi tiết giáo trình theo từng chương
                  const bookChapterRows = [];
                  const courseContents = elearningCoursesContents?.[selectedCourse] || {};
                  Object.values(courseContents).forEach((mod) => {
                    if (mod?.modname !== 'book') return;
                    const bookName = mod?.name || `Giáo trình ${mod?.id}`;
                    // Lấy danh sách chương từ contents (index.html files)
                    const chapters = (mod?.contents || [])
                      .filter((c) => c?.filename === 'index.html')
                      .map((c, idx) => {
                        const chapterId = Number(c?.fileurl?.split('/')?.at(-2));
                        return { chapterId, title: c?.content || `Chương ${idx + 1}` };
                      })
                      .filter((ch) => ch.chapterId > 0);

                    chapters.forEach(({ chapterId, title }, idx) => {
                      // Tìm record bookMapaData cho chapter này
                      const record = (bookMapaData || []).find(
                        (r) => r?.chapterId === chapterId && r?.moduleId === mod?.id
                      );
                      if (!record) return;
                      const chapterSettings = bookModuleSettings?.[mod?.id]?.[chapterId];
                      const chapterInterval = chapterSettings?.interval || 30;
                      const readingTimeMin = chapterSettings?.readingTime;

                      // Chương chưa cấu hình readingTime → 0 (khớp backend)
                      if (!readingTimeMin || readingTimeMin <= 0) {
                        bookChapterRows.push({
                          key: `${mod?.id}-${chapterId}`,
                          bookName,
                          chapterLabel: title,
                          timeSpent: 0,
                          noReadingTime: true,
                        });
                        return;
                      }

                      const capSeconds = readingTimeMin * 60;
                      let timeSpent = 0;
                      if (Array.isArray(record.mapa) && record.mapa.length > 0) {
                        // Lọc entries hôm nay, sắp xếp theo thời gian, cap tại readingTime × 60
                        const todayEntries = record.mapa
                          .filter((ts) => ts > 0 && isTimestampTodayUTC7(ts * 1000))
                          .sort((a, b) => a - b);
                        for (const ts of todayEntries) {
                          const remaining = capSeconds - timeSpent;
                          if (remaining <= 0) break;
                          timeSpent += Math.min(chapterInterval, remaining);
                        }
                      }
                      if (timeSpent > 0) {
                        bookChapterRows.push({
                          key: `${mod?.id}-${chapterId}`,
                          bookName,
                          chapterLabel: title,
                          timeSpent,
                        });
                      }
                    });
                  });

                  return (
                    <StyledTableWrap>
                      <Table striped bordered hover size='sm' responsive className='mb-0'>
                        <thead>
                          <tr>
                            <th>Hoạt động</th>
                            <th>Loại</th>
                            <th>Thời gian hôm nay</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows
                            .filter((r) => r.timeSpent > 0)
                            .map(({ mod, timeSpent }) => (
                              <tr key={mod.cmid}>
                                <td>{mod.itemname || mod.svname || `Module ${mod.cmid}`}</td>
                                <td>{COURSE_MODULES[mod.modname] || mod.modname}</td>
                                <td>{formatTime(timeSpent)}</td>
                              </tr>
                            ))}
                          {bookChapterRows
                            .filter((row) => row.timeSpent > 0 || row.noReadingTime)
                            .map((row) => (
                            <tr key={row.key}>
                              <td>{row.bookName} — {row.chapterLabel}</td>
                              <td>{COURSE_MODULES['book'] || 'Giáo trình'}</td>
                              <td>{formatTime(row.timeSpent)}</td>
                            </tr>
                          ))}
                          {rows.filter((r) => r.timeSpent > 0).length === 0 && bookChapterRows.length === 0 && (
                            <tr>
                              <td colSpan={3} className='text-center text-muted'>
                                Không có dữ liệu học hôm nay cho môn này
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </StyledTableWrap>
                  );
                })()}
              </Modal.Body>
              <Modal.Footer>
                <PrimaryButton variant='secondary' onClick={() => setSelectedCourse(null)}>
                  Đóng
                </PrimaryButton>
              </Modal.Footer>
            </Modal>
          </>
        )}
      </Container>
    </PageWrap>
  );
}

/* ─── styled ─── */

const PageWrap = styled.div`
  min-height: 100vh;
  background: ${colors.background};
  overflow-y: auto;
`;

const PageTitle = styled.h2`
  font-size: ${typography.size.h2};
  font-weight: ${typography.weight.bold};
  color: ${colors.gray900};
  margin: ${(p) => (p.$spaced ? `${spacing[8]} 0 ${spacing[5]} 0` : `0`)};

  ${MOBILE} {
    font-size: ${typography.size.h3};
    margin: ${(p) => (p.$spaced ? `${spacing[6]} 0 ${spacing[4]} 0` : `0`)};
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${spacing[2]};
  margin-bottom: ${spacing[3]};
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
`;

// Pill rõ ràng hơn UpdatedAtText cũ (font xs / gray400 → mờ, dễ bỏ qua).
// Icon đồng hồ + nền surfaceMuted + bold timestamp → ánh nhìn lập tức
// thấy. Meta line "Tự cập nhật mỗi 5 phút" giúp HV hiểu data refresh
// theo interval, không bị nhầm là không cập nhật.
const UpdatedAtPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  padding: ${spacing[1]} ${spacing[3]};
  background: ${colors.surfaceMuted};
  border: 1px solid ${colors.border};
  border-radius: ${radius.pill || radius.lg};
  color: ${colors.gray700};
  font-size: ${typography.size.sm};

  > svg {
    color: ${colors.gray500};
    flex-shrink: 0;
  }

  strong {
    color: ${colors.gray900};
    font-weight: ${typography.weight.semibold};
  }
`;

const UpdatedAtMeta = styled.span`
  color: ${colors.gray500};
  font-size: ${typography.size.xs};
  margin-left: ${spacing[1]};
`;

// Cabin booking CTA card. Always visible (HV may not know exactly when
// they're "complete"); the actual gate is BE-side via Mongo
// elearningStatus. Friendly toast on 403 NOT_COMPLETED guides HV back to
// finishing the course. Layout mirrors NoticeCard pattern for
// consistency, with success colour + filled CTA on the right.
const CabinCard = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  background: ${colors.successSoft || '#dcfce7'};
  border: 1px solid ${colors.success};
  border-left: 4px solid ${colors.success};
  border-radius: ${radius.md};
  padding: ${spacing[3]} ${spacing[4]};
  margin: ${spacing[3]} 0;

  ${MOBILE} {
    flex-direction: column;
    align-items: stretch;
  }
`;

const CabinIcon = styled.div`
  color: ${colors.success};
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const CabinBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const CabinTitle = styled.div`
  font-weight: ${typography.weight.semibold};
  color: ${colors.gray900};
  font-size: ${typography.size.md};
  margin-bottom: ${spacing[1]};
`;

const CabinDescription = styled.div`
  color: ${colors.gray700};
  font-size: ${typography.size.sm};
  line-height: ${typography.leading.relaxed};
`;

// Notice card cho ghi chú quan trọng — nổi bật hơn helper text mờ
// trước đây. Pattern: icon trái + body có title + bullet list, nền
// warning nhạt + viền trái accent màu warning đậm để mắt bắt ngay.
const NoticeCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing[3]};
  background: ${colors.warningSoft};
  border: 1px solid ${colors.warning};
  border-left: 4px solid ${colors.warning};
  border-radius: ${radius.md};
  padding: ${spacing[3]} ${spacing[4]};
  margin: ${spacing[3]} 0;
`;

const NoticeIcon = styled.div`
  color: ${colors.warning};
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-top: 2px;
`;

const NoticeBody = styled.div`
  flex: 1;
  min-width: 0;

  ul {
    margin: ${spacing[2]} 0 0 0;
    padding-left: ${spacing[5]};
    color: ${colors.gray800};
    font-size: ${typography.size.sm};
    line-height: ${typography.leading.relaxed};
  }

  li + li {
    margin-top: ${spacing[2]};
  }
`;

const StyledTableWrap = styled.div`
  background: ${colors.surface};
  border: 1px solid ${colors.border};
  border-radius: ${radius.lg};
  box-shadow: ${shadow.sm};
  /* KHÔNG dùng overflow:hidden — sẽ clip horizontal scroll của
     .table-responsive ở mobile. Dùng overflow-y hidden để rounded
     border vẫn được giữ cho top/bottom, overflow-x visible cho
     scroll bar hiện đúng. */
  overflow-y: hidden;
  overflow-x: visible;
  margin-bottom: ${spacing[5]};

  /* react-bootstrap <Table responsive> wrap trong .table-responsive
     với overflow-x: auto. Ensure border-radius nội thất khớp wrap. */
  .table-responsive {
    border-radius: inherit;
  }

  table {
    margin-bottom: 0;
    color: ${colors.gray800};
    font-size: ${typography.size.base};
  }

  thead th {
    background: ${colors.surfaceMuted};
    color: ${colors.gray700};
    font-weight: ${typography.weight.semibold};
    font-size: ${typography.size.sm};
    border-color: ${colors.border};
    white-space: nowrap;
  }

  tbody td {
    border-color: ${colors.border};
    vertical-align: middle;
  }
`;

const LoadingBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${spacing[6]} 0;
`;

const LoadingChecklist = styled.div`
  margin-top: ${spacing[4]};
  min-width: 280px;
`;

const LoadingChecklistItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  margin-bottom: ${spacing[2]};
  font-size: ${typography.size.sm};
  color: ${({ $loaded }) => ($loaded ? colors.gray500 : colors.gray800)};
`;

const CheckMark = styled.span`
  color: ${colors.success};
  font-weight: ${typography.weight.bold};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
`;

const ModalText = styled.p`
  margin-bottom: ${spacing[3]};
  color: ${colors.gray800};
  font-size: ${typography.size.base};
`;

const NoticeBox = styled.div`
  background: ${colors.warningSoft};
  border: 1px solid ${colors.warning};
  border-radius: ${radius.md};
  padding: ${spacing[3]} ${spacing[4]};
  font-size: ${typography.size.base};
`;

const NoticeTitle = styled.strong`
  color: ${colors.warning};
`;

const NoticeList = styled.ul`
  margin: ${spacing[2]} 0 0 0;
  padding-left: ${spacing[5]};
  color: ${colors.gray700};
`;

export default ElearningStudentResultPage;
